import React, { useState, useEffect } from 'react';
import { 
  StyleSheet, 
  View, 
  Text, 
  ScrollView, 
  FlatList,
  TouchableOpacity, 
  Alert, 
  ActivityIndicator 
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { 
  doc, 
  getDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy 
} from 'firebase/firestore';
import { auth, firestore } from '../../services/firebase';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { colors, typography, spacing, borderRadius } from '../../constants/theme';
import { Ionicons } from '@expo/vector-icons';
import Card from '../../components/Card';
import { useTheme } from '../../context/ThemeContext';

type Props = NativeStackScreenProps<MainStackParamList, 'WeightTracking'>;

interface ProgressRecord {
  id: string;
  weight: number;
  series: number;
  reps: number;
  date: any;
  routineId: string;
  routineName?: string;
  notes?: string;
}

interface ChartDataPoint {
  date: Date;
  weight: number;
}

const WeightTrackingScreen = ({ route, navigation }: Props) => {
  const { exerciseId } = route.params;
  const [exercise, setExercise] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [progressHistory, setProgressHistory] = useState<ProgressRecord[]>([]);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const { theme } = useTheme();

  useEffect(() => {
    fetchExerciseDetails();
    fetchProgressHistory();
  }, [exerciseId]);

  const fetchExerciseDetails = async () => {
    try {
      const exerciseRef = doc(firestore, 'exercises', exerciseId);
      const exerciseDoc = await getDoc(exerciseRef);
      
      if (!exerciseDoc.exists()) {
        Alert.alert('Error', 'No se encontró el ejercicio');
        navigation.goBack();
        return;
      }
      
      setExercise({
        id: exerciseDoc.id,
        ...exerciseDoc.data()
      });
    } catch (error) {
      console.error('Error al obtener detalles del ejercicio:', error);
      Alert.alert('Error', 'No se pudieron cargar los detalles del ejercicio');
    } finally {
      setLoading(false);
    }
  };

  const fetchProgressHistory = async () => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }
      
      const progressRef = collection(firestore, 'progress');
      const q = query(
        progressRef,
        where('userId', '==', userId),
        where('exerciseId', '==', exerciseId),
        orderBy('date', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const history: ProgressRecord[] = [];
      const chartPoints: ChartDataPoint[] = [];
      
      // Obtener IDs de rutinas para hacer un segundo query
      const routineIds = new Set<string>();
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        routineIds.add(data.routineId);
        
        const record = {
          id: doc.id,
          ...data
        } as ProgressRecord;
        
        history.push(record);
        
        // Add to chart data (converting to Date object)
        chartPoints.push({
          date: data.date.toDate(),
          weight: data.weight
        });
      });
      
      // Sort chart data chronologically (oldest to newest)
      chartPoints.sort((a, b) => a.date.getTime() - b.date.getTime());
      
      // Obtener nombres de rutinas si hay registros
      if (routineIds.size > 0) {
        const routinesData: {[key: string]: string} = {};
        
        await Promise.all(
          Array.from(routineIds).map(async (routineId) => {
            try {
              const routineDoc = await getDoc(doc(firestore, 'routines', routineId));
              if (routineDoc.exists()) {
                routinesData[routineId] = routineDoc.data().name;
              }
            } catch (error) {
              console.error(`Error getting routine ${routineId}:`, error);
            }
          })
        );
        
        // Añadir nombres de rutinas a los registros
        history.forEach(record => {
          record.routineName = routinesData[record.routineId] || 'Rutina desconocida';
        });
      }
      
      setProgressHistory(history);
      setChartData(chartPoints);
    } catch (error) {
      console.error('Error al obtener historial de progreso:', error);
      Alert.alert('Error', 'No se pudo cargar el historial de progreso');
    }
  };

  const formatDate = (date: any) => {
    const d = date.toDate();
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
  };

  if (loading) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  if (!exercise) {
    return (
      <View style={[styles.errorContainer, { backgroundColor: theme.colors.background }]}>
        <Text style={[styles.errorText, { color: theme.colors.error }]}>No se pudo cargar el ejercicio</Text>
      </View>
    );
  }

  const renderWeightChart = () => {
    // Simple chart representation with dots and lines
    if (chartData.length === 0) return null;
    
    const maxWeight = Math.max(...chartData.map(point => point.weight));
    const minWeight = Math.min(...chartData.map(point => point.weight));
    const range = maxWeight - minWeight;
    const padding = range * 0.1; // Add 10% padding
    
    const chartHeight = 150;
    const chartTop = minWeight - padding;
    const chartBottom = maxWeight + padding;
    const chartRange = chartBottom - chartTop;
    
    return (
      <View style={styles.chartContainer}>
        <View style={styles.chartYAxis}>
          <Text style={[styles.chartAxisLabel, { color: theme.colors.grey.dark }]}>{Math.round(chartBottom)} kg</Text>
          <Text style={[styles.chartAxisLabel, { color: theme.colors.grey.dark }]}>{Math.round((chartBottom + chartTop) / 2)} kg</Text>
          <Text style={[styles.chartAxisLabel, { color: theme.colors.grey.dark }]}>{Math.round(chartTop)} kg</Text>
        </View>
        
        <View style={[styles.chartContent, { borderColor: theme.colors.grey.light }]}>
          {chartData.map((point, index) => {
            // Calculate position based on weight value
            const heightPercent = 1 - ((point.weight - chartTop) / chartRange);
            const yPosition = heightPercent * chartHeight;
            
            // Calculate the horizontal position
            const width = 100 / (chartData.length - 1 || 1);
            const xPosition = index * width;
            
            return (
              <View 
                key={index}
                style={[
                  styles.chartDot,
                  {
                    left: `${xPosition}%`,
                    top: yPosition,
                    backgroundColor: theme.colors.primary
                  }
                ]}
              >
                {/* Draw line to next point if not the last point */}
                {index < chartData.length - 1 && (
                  <View style={[styles.chartLine, { backgroundColor: theme.colors.primary }]} />
                )}
              </View>
            );
          })}
        </View>
      </View>
    );
  };

  const renderHistoryItem = ({ item }: { item: ProgressRecord }) => (
    <Card style={styles.historyItem}>
      <View style={styles.historyHeader}>
        <Text style={[styles.historyDate, { color: theme.colors.text }]}>{formatDate(item.date)}</Text>
        <Text style={[styles.historyRoutine, { color: theme.colors.grey.dark }]}>{item.routineName}</Text>
      </View>
      
      <View style={styles.historyDetails}>
        <View style={styles.historyDetail}>
          <Text style={[styles.detailValue, { color: theme.colors.primary }]}>{item.weight} kg</Text>
          <Text style={[styles.detailLabel, { color: theme.colors.grey.dark }]}>Peso</Text>
        </View>
        
        <View style={styles.historyDetail}>
          <Text style={[styles.detailValue, { color: theme.colors.primary }]}>{item.series}</Text>
          <Text style={[styles.detailLabel, { color: theme.colors.grey.dark }]}>Series</Text>
        </View>
        
        <View style={styles.historyDetail}>
          <Text style={[styles.detailValue, { color: theme.colors.primary }]}>{item.reps}</Text>
          <Text style={[styles.detailLabel, { color: theme.colors.grey.dark }]}>Reps</Text>
        </View>
      </View>
      
      {item.notes && (
        <View style={[styles.notesContainer, { borderTopColor: theme.colors.grey.light }]}>
          <Text style={[styles.notesLabel, { color: theme.colors.grey.dark }]}>Notas:</Text>
          <Text style={[styles.notesText, { color: theme.colors.text }]}>{item.notes}</Text>
        </View>
      )}
    </Card>
  );

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.header}>
        <View>
          <Text style={[styles.exerciseName, { color: theme.colors.text }]}>{exercise.name}</Text>
          <View style={styles.muscleGroups}>
            {exercise.targetMuscles && exercise.targetMuscles.slice(0, 3).map((muscle: string, index: number) => (
              <Text key={index} style={[styles.muscleText, { color: theme.colors.grey.dark }]}>
                {muscle}{index < Math.min(exercise.targetMuscles.length, 3) - 1 ? ', ' : ''}
              </Text>
            ))}
            {exercise.targetMuscles && exercise.targetMuscles.length > 3 && (
              <Text style={[styles.muscleText, { color: theme.colors.grey.dark }]}>...</Text>
            )}
          </View>
        </View>
        
        <TouchableOpacity
          style={[styles.detailButton, { backgroundColor: theme.colors.primary }]}
          onPress={() => navigation.navigate('ExerciseLibraryDetail', { exerciseId })}
        >
          <Text style={[styles.detailButtonText, { color: theme.colors.white }]}>Ver Detalles</Text>
        </TouchableOpacity>
      </View>
      
      <Card style={styles.chartCard}>
        <Text style={[styles.cardTitle, { color: theme.colors.text }]}>Progresión de Peso</Text>
        
        {chartData.length > 0 ? (
          renderWeightChart()
        ) : (
          <View style={styles.emptyChartContainer}>
            <Text style={[styles.emptyText, { color: theme.colors.grey.dark }]}>No hay suficientes datos para mostrar la progresión</Text>
          </View>
        )}
      </Card>
      
      <Text style={[styles.historyTitle, { color: theme.colors.text }]}>Historial de Registros</Text>
      
      {progressHistory.length > 0 ? (
        <FlatList
          data={progressHistory}
          renderItem={renderHistoryItem}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.historyList}
        />
      ) : (
        <View style={styles.emptyHistoryContainer}>
          <Text style={[styles.emptyText, { color: theme.colors.grey.dark }]}>No hay registros de progreso para este ejercicio</Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
    padding: spacing.lg,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  errorText: {
    fontSize: typography.fontSize.lg,
    color: colors.error,
    textAlign: 'center',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  exerciseName: {
    fontSize: typography.fontSize.xl,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: spacing.xs,
  },
  muscleGroups: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  muscleText: {
    fontSize: typography.fontSize.sm,
    color: colors.grey.dark,
  },
  detailButton: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.primary,
    borderRadius: borderRadius.sm,
  },
  detailButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.sm,
    fontWeight: '500',
  },
  chartCard: {
    marginBottom: spacing.lg,
    padding: spacing.md,
  },
  cardTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: spacing.md,
  },
  chartContainer: {
    height: 200,
    flexDirection: 'row',
  },
  chartYAxis: {
    width: 50,
    height: '100%',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
  },
  chartAxisLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.grey.dark,
    textAlign: 'right',
    paddingRight: spacing.xs,
  },
  chartContent: {
    flex: 1,
    height: 150,
    borderLeftWidth: 1,
    borderBottomWidth: 1,
    borderColor: colors.grey.light,
    position: 'relative',
    marginBottom: spacing.lg,
  },
  chartDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.primary,
    position: 'absolute',
    transform: [{ translateX: -4 }, { translateY: -4 }],
  },
  chartLine: {
    position: 'absolute',
    height: 2,
    backgroundColor: colors.primary,
    width: 100, // This will be overridden by percentage width
    top: 3,
    left: 4,
    zIndex: -1,
  },
  emptyChartContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    height: 150,
  },
  historyTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: spacing.md,
  },
  historyList: {
    paddingBottom: spacing.lg,
  },
  historyItem: {
    marginBottom: spacing.md,
  },
  historyHeader: {
    marginBottom: spacing.md,
  },
  historyDate: {
    fontSize: typography.fontSize.md,
    fontWeight: '500',
    color: colors.text,
  },
  historyRoutine: {
    fontSize: typography.fontSize.sm,
    color: colors.grey.dark,
  },
  historyDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.md,
  },
  historyDetail: {
    alignItems: 'center',
  },
  detailValue: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    color: colors.primary,
  },
  detailLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.grey.dark,
    marginTop: 2,
  },
  notesContainer: {
    borderTopWidth: 1,
    borderTopColor: colors.grey.light,
    paddingTop: spacing.sm,
  },
  notesLabel: {
    fontSize: typography.fontSize.sm,
    fontWeight: '500',
    color: colors.grey.dark,
    marginBottom: spacing.xs,
  },
  notesText: {
    fontSize: typography.fontSize.sm,
    color: colors.text,
  },
  emptyHistoryContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyText: {
    fontSize: typography.fontSize.md,
    color: colors.grey.dark,
    textAlign: 'center',
    marginVertical: spacing.md,
  },
});

export default WeightTrackingScreen; 